import { useEffect, useMemo, useState } from "react";
import { X, PartyPopper } from "lucide-react";

const STORAGE_PREFIX = "vu_streak_celebrated_";
const MILESTONES = [7, 30, 100];

const PIECE_COLORS = ["#7B2D8E", "#E91E63", "#FF6B35", "#FFC857", "#2EC4B6"];

function makePieces(count) {
    return Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 2.2 + Math.random() * 1.6,
        rotate: Math.random() * 360,
        color: PIECE_COLORS[i % PIECE_COLORS.length],
        size: 6 + Math.random() * 8,
    }));
}

export default function StreakCelebration({ streak }) {
    const [show, setShow] = useState(false);
    const [milestone, setMilestone] = useState(null);
    const pieces = useMemo(() => makePieces(80), [milestone]);

    useEffect(() => {
        const current = streak?.current ?? 0;
        if (!current) return;
        // Find the highest milestone reached today that hasn't been celebrated
        const eligible = MILESTONES.filter((m) => current >= m);
        const target = eligible.length ? eligible[eligible.length - 1] : null;
        if (!target) return;
        const key = `${STORAGE_PREFIX}${target}`;
        if (typeof window === "undefined") return;
        if (localStorage.getItem(key)) return;
        setMilestone(target);
        setShow(true);
        localStorage.setItem(key, new Date().toISOString());
    }, [streak?.current]);

    if (!show || !milestone) return null;

    const headline =
        milestone === 7 ? "One week unstoppable."
        : milestone === 30 ? "A whole month of momentum."
        : "One hundred days. Phenomenal.";

    return (
        <div
            className="fixed inset-0 z-[70] grid place-items-center bg-black/40 backdrop-blur-sm p-4"
            data-testid="streak-celebration"
            role="dialog"
            aria-live="polite"
        >
            {/* Confetti layer */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                {pieces.map((p) => (
                    <span
                        key={p.id}
                        className="vu-confetti-piece"
                        style={{
                            left: `${p.left}%`,
                            background: p.color,
                            width: p.size,
                            height: p.size * 0.4,
                            transform: `rotate(${p.rotate}deg)`,
                            animationDelay: `${p.delay}s`,
                            animationDuration: `${p.duration}s`,
                        }}
                    />
                ))}
            </div>

            <div className="card-warm p-7 max-w-md w-full text-center relative fade-up" data-testid="streak-celebration-card">
                <button
                    onClick={() => setShow(false)}
                    aria-label="Close celebration"
                    data-testid="streak-celebration-close"
                    className="absolute top-3 right-3 text-[var(--text-ink)]/50 hover:text-[var(--brand-magenta)]"
                >
                    <X size={16} />
                </button>
                <PartyPopper size={42} className="mx-auto text-[var(--brand-magenta)]" />
                <div className="font-script text-[var(--brand-orange)] text-2xl mt-2">you&rsquo;re unstoppable</div>
                <h2 className="font-serif text-3xl mt-1">{headline}</h2>
                <p className="text-sm text-[var(--text-ink)]/70 mt-3 leading-relaxed">
                    You&rsquo;ve shown up <strong>{milestone} days in a row</strong>. Small consistent moves compound into massive change — keep the flame alive.
                </p>
                <button
                    onClick={() => setShow(false)}
                    data-testid="streak-celebration-cta"
                    className="btn-primary mt-5 w-full"
                >
                    Keep going
                </button>
            </div>

            <style>{`
                .vu-confetti-piece {
                    position: absolute;
                    top: -20px;
                    border-radius: 2px;
                    animation-name: vu-confetti-fall;
                    animation-iteration-count: 1;
                    animation-timing-function: cubic-bezier(.2,.4,.6,1);
                    animation-fill-mode: forwards;
                    opacity: 0.95;
                }
                @keyframes vu-confetti-fall {
                    0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
                    80% { opacity: 1; }
                    100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .vu-confetti-piece { display: none; }
                }
            `}</style>
        </div>
    );
}
